import { FormEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Paperclip, X } from 'react-feather';
import { useIntl } from 'react-intl';
import { useStreaming } from 'src/context/StreamingContext';
import { useSubmitMessage } from 'src/hooks/api';
import { UserMessage } from 'src/types';
import { useAppSelector } from 'universal-scripts';

import './Footer.sass';

async function url2blob(url: string) {
	try {
		const response = await fetch(url);
		const contentType = response.headers.get('Content-Type') || '';

		const buffer = await response.arrayBuffer();
		const blob = new Blob([buffer], {
			type: contentType
		});
		const file = new File([blob], url.substring(url.lastIndexOf('/') + 1), {
			type: blob.type
		});

		return file;
	} catch (err) {
		return undefined;
	}
}

interface FooterProps {
	editing: UserMessage | null;
	disableEditing: () => void;
	onChunk: (chunk: string) => void;
	onComplete: (fullText: string) => void;
}

const Footer = ({ editing, disableEditing, onChunk, onComplete }: FooterProps) => {
	const [uploadedFile, setUploadedFile] = useState<File | null>(null);
	const intl = useIntl();
	const labelRef = useRef<HTMLLabelElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const [text, setText] = useState<string>('');
	const width = useAppSelector((s) => s.width);
	const messages = useAppSelector((s) => s.messages);
	const lastMessage = messages[messages.length - 1];
	const ready = lastMessage && 'completed' in lastMessage ? lastMessage.completed : true;

	const { streamInfo } = useStreaming();
	const { isWriting } = streamInfo;

	const isMobile = width && width < 768;

	const onDrop = useCallback((acceptedFiles: File[]) => {
		if (acceptedFiles?.[0]) {
			setUploadedFile(acceptedFiles[0]);
		}
	}, []);

	const { getRootProps, getInputProps } = useDropzone({
		onDrop,
		accept: {
			'image/png': ['.png', '.jpeg', '.jpg', '.webp', '.heic', '.heif']
		},
		multiple: false
	});

	const submit = useSubmitMessage({
		userInput: text,
		file: uploadedFile,
		onChunk,
		onComplete
	});

	const disabled = isWriting || (!!messages.length && !ready);

	useEffect(() => {
		if (!isWriting && ready && !isMobile && inputRef && inputRef.current) {
			inputRef.current.focus();
		}
	}, [isWriting, isMobile, ready]);

	const handleEditImage = async (file: File | string) => {
		const fileToSet = typeof file === 'string' ? ((await url2blob(file)) ?? null) : file;
		setUploadedFile(fileToSet);
	};

	useEffect(() => {
		if (!editing) {
			setText('');
			return disableEditing();
		}
		setText(editing.text);
		if (editing?.file) {
			handleEditImage(editing.file);
		}
	}, [editing, disableEditing]);

	const submitMessage = async (e: FormEvent<HTMLFormElement> | KeyboardEvent<HTMLTextAreaElement>): Promise<void> => {
		e.preventDefault();
		if (!text.length) return;
		await submit();
		setText('');
		setUploadedFile(null);
		if (labelRef && labelRef.current) labelRef.current.dataset.value = '';
		if (editing) disableEditing();
	};

	const handleInput = (e: FormEvent<HTMLTextAreaElement>) => {
		setText(e.currentTarget.value);
		if (labelRef && labelRef.current) labelRef.current.dataset.value = e.currentTarget.value;
	};

	return (
		<footer id="chat-footer">
			<form onSubmit={submitMessage}>
				<section>
					{uploadedFile && (
						<div className="uploaded-file">
							<div>
								<div onClick={() => setUploadedFile(null)}>
									<X color="#404040" size={16} />
								</div>
								<img alt="preview" width={70} height={100} src={URL.createObjectURL(uploadedFile)} />
							</div>
						</div>
					)}
					<label ref={labelRef} className={`${text ? 'filled' : ''}`}>
						<textarea
							name="message"
							ref={inputRef}
							onKeyDown={(e) => e.key === 'Enter' && submitMessage(e)}
							onInput={handleInput}
							autoFocus={!text && !isMobile}
							rows={1}
							disabled={disabled}
							placeholder={intl.formatMessage({ id: 'placeholder' })}
							value={text}
						/>
						{disabled ? (
							<div className="file" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
								<button disabled type="button">
									<Paperclip />
								</button>
							</div>
						) : (
							<div className="file" {...getRootProps()}>
								<input {...getInputProps()} />
								<button type="button">
									<Paperclip />
								</button>
							</div>
						)}
					</label>
				</section>
				<button>
					<span className="icon icon-arrow-block" />
				</button>
			</form>
		</footer>
	);
};

export default Footer;
